/**
 * 查询索引。所有映射都从 Concept 数组派生，没有第二处登记题目的地方。
 * Library 只读，重建即替换，永不原地改。
 * 只用 model，由 parsing 构造一次，之后 plan 与屏幕只读它。
 * 不解析文本，不改条目，也不决定复习顺序。
 */

import type { Concept, Tag } from '../model/concept'
import type { AnyQuestion } from '../model/question'
import type { CardKey, CardRef, ConceptId, QuestionId, RepoPath } from '../model/ids'
import { cardKey, conceptCard, conceptOfQuestion, questionCard } from '../model/ids'

export interface Library {
	readonly concepts: readonly Concept[]
	readonly conceptCount: number
	readonly questionCount: number
	/** 每个条目都有知识点卡，所以卡片总数 = 条目数 + 题目数。 */
	readonly cardCount: number
	concept(id: ConceptId): Concept | undefined
	conceptByPath(path: RepoPath): Concept | undefined
	question(id: QuestionId): AnyQuestion | undefined
	/** 条目下的题目，顺序与文件书写顺序一致。 */
	questionsOf(conceptId: ConceptId): readonly AnyQuestion[]
	byTag(tag: Tag): readonly Concept[]
	/** 别名与标题的搜索命中，派生自 Concept.aliases，不另存一份。 */
	byAlias(alias: string): readonly Concept[]
	/** 当前仓库里存在的卡片键，foldReviews 用它挑出孤儿记录。 */
	cardKeys(): ReadonlySet<CardKey>
}

/** 一张表存多值：一个标签、一个别名都可能挂着多个条目。 */
function addTo<Key, Value>(table: Map<Key, Value[]>, key: Key, value: Value): void {
	const bucket = table.get(key)
	if (bucket === undefined) table.set(key, [value])
	else bucket.push(value)
}

/** 一次遍历建六张派生表。空数组进来得到空表，所以 EMPTY_LIBRARY 走的是同一个构造点。 */
export function buildLibrary(concepts: readonly Concept[]): Library {
	const byId = new Map<ConceptId, Concept>()
	const byPath = new Map<RepoPath, Concept>()
	const questionById = new Map<QuestionId, AnyQuestion>()
	const questionsByConcept = new Map<ConceptId, AnyQuestion[]>()
	const tagIndex = new Map<Tag, Concept[]>()
	const aliasIndex = new Map<string, Concept[]>()
	const keys = new Set<CardKey>()
	let questionCount = 0

	for (const concept of concepts) {
		byId.set(concept.id, concept)
		byPath.set(concept.path, concept)
		questionsByConcept.set(concept.id, [...concept.questions])
		keys.add(cardKey(conceptCard(concept.id)))
		for (const question of concept.questions) {
			questionById.set(question.id, question)
			keys.add(cardKey(questionCard(question.id)))
			questionCount += 1
		}
		for (const tag of concept.tags) addTo(tagIndex, tag, concept)
		for (const alias of concept.aliases) addTo(aliasIndex, alias, concept)
	}

	return {
		concepts,
		conceptCount: concepts.length,
		questionCount,
		cardCount: concepts.length + questionCount,
		concept: (id) => byId.get(id),
		conceptByPath: (path) => byPath.get(path),
		question: (id) => questionById.get(id),
		questionsOf: (conceptId) => questionsByConcept.get(conceptId) ?? [],
		byTag: (tag) => tagIndex.get(tag) ?? [],
		byAlias: (alias) => aliasIndex.get(alias) ?? [],
		cardKeys: () => keys,
	}
}

/** 首次 reload 之前的空库。走同一个构造点，所以空库和真库不会有第二套形状。 */
export const EMPTY_LIBRARY: Library = buildLibrary([])

/** 队列与统计把 CardRef 还原成可展示的内容，这里是唯一的解引用点。 */
export function resolveCard(
	library: Library,
	ref: CardRef,
): { readonly concept: Concept; readonly question: AnyQuestion | null } | null {
	if (ref.kind === 'concept') {
		const concept = library.concept(ref.conceptId)
		return concept === undefined ? null : { concept, question: null }
	}
	const question = library.question(ref.questionId)
	if (question === undefined) return null
	// 题目卡进来时条目一定同在：两者由同一次解析同时写进索引，缺任何一边都说明这张卡不属于当前仓库。
	const concept = library.concept(conceptOfQuestion(question.id))
	return concept === undefined ? null : { concept, question }
}
